import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Text,
    Icon,
    TabList,
    Tabs,
    Tab,
    TabPanels,
    TabPanel,
    Flex,
    Button,
    Spinner,
    Alert,
    AlertIcon,
    Tag,
    HStack,
    useToast
} from "@chakra-ui/react";
import Select from "react-select";
import { FiCheckCircle, FiFileText, FiMessageSquare, FiTag } from "react-icons/fi";
import {  } from "react-icons/bs";
import {  } from "react-icons/md";
import {  } from "react-icons/md";
import {  } from "react-icons/md";
import Header from "../../../components/Header";
import CampaignOverview, { MessagePreview } from "./CampaignOverview/CampaignOverview";
import PerformanceChart from "./CampaignPerformance/CampaignPerformance";

import {  } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { fetchWrapper } from "../../../helpers";
import ChannelsTable from "./Channels/Channels";
import MessageDetailsTable from "./MessageDetails/MessageDetails";
import MetricsCards from "./MetricsCard/MetricsCard";
import CustomBadge from "../../../components/SharedComponent/Badge";
import {  } from "../../../utils/DefaultFunction";
import { LuArrowLeft } from "react-icons/lu";
import { CHANNELS, CHANNEL_IDS } from "../../../utils/channels";
import BoxWrapper from "../../../components/BoxWrapper/BoxWrapper";
// LOCAL ONLY — AI Agents integration
// FEAT-0012 — AI Insights is now a tab (AiInsightsTab), not a modal.
// The previous CampaignPerformanceModal import has been removed; the
// component file remains in the repo for reference but is no longer
// rendered from this page.
import AiInsightsTab from "./AiInsightsTab";
// FEAT-ATTRIB-003 A2 — the Impact tab. Gated on the attribution service being
// configured, exactly as AI Insights is gated on its flag, so Chakra always
// sees an equal number of Tabs and TabPanels.
import ImpactTab from "./ImpactTab";
import { AI_INSIGHTS_ENABLED, ATTRIBUTION_ENABLED } from "../../../utils/featureFlags";

export default function CampaignStats({ header }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useToast();
    // LOCAL ONLY — AI Agents state
    // FEAT-0012 — AI Insights moved from a modal to a tab; no
    // open/close state needed anymore.

    // Stats data (for metrics cards)
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    // DA-BUG-0231 — surface stats fetch errors so the operator sees
    // why the Campaign Stats panel is empty rather than silently
    // logging to console.
    const [statsError, setStatsError] = useState(null);

    // Overview data (for campaign info section and overview tab)
    const [overviewData, setOverviewData] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [overviewError, setOverviewError] = useState(null);

    // Performance data
    const [performanceData, setPerformanceData] = useState(null);
    const [performanceLoading, setPerformanceLoading] = useState(false);
    const [performanceError, setPerformanceError] = useState(null);

    // Channels data
    const [channelsData, setChannelsData] = useState(null);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [channelsError, setChannelsError] = useState(null);

    // Message details data
    const [messageData, setMessageData] = useState(null);
    const [messageLoading, setMessageLoading] = useState(false);
    const [messageError, setMessageError] = useState(null);

    const [activeTab, setActiveTab] = useState(0);

    // Conversion event state
    const [conversionEvents, setConversionEvents] = useState([]);
    const [selectedConversionEvent, setSelectedConversionEvent] = useState(null);

    useEffect(() => {
        loadStatsData();
        loadOverviewData();
        loadConversionEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (statsData?.conversion_event?.id && conversionEvents.length > 0) {
            const match = conversionEvents.find((o) => o.value === statsData.conversion_event.id);
            if (match) setSelectedConversionEvent(match);
        }
    }, [statsData, conversionEvents]);

    const loadStatsData = async () => {
        // DA-BUG-0231 — bypass fetchWrapper here so we can see the
        // exact HTTP status + body when the call fails. fetchWrapper's
        // handleResponse rejects with a bare string and drops the
        // status code entirely; that swallowed the diagnostic info
        // we need. This direct fetch carries the same x-access-token
        // / x-api-key headers fetchWrapper would have added.
        try {
            setStatsLoading(true);
            setStatsError(null);
            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}/analytics/stats`;
            const r = await fetch(url, {
                method: "GET",
                headers: {
                    "x-api-key": localStorage.getItem("api-key") || "",
                    "x-access-token": localStorage.getItem("customer-portal") || "",
                    "Content-Type": "application/json",
                },
            });
            const rawText = await r.text();
            let payload = null;
            try { payload = rawText ? JSON.parse(rawText) : null; } catch { /* leave as raw */ }

            if (!r.ok) {
                const backendMsg = payload?.message || payload?.error || r.statusText || `HTTP ${r.status}`;
                setStatsError(`HTTP ${r.status} — ${backendMsg}${rawText && !payload ? ` · raw: ${rawText.slice(0, 200)}` : ""}`);
                return;
            }
            if (payload?.success) {
                setStatsData(payload.data);
            } else {
                setStatsError(payload?.message || `200 OK but response.success=false (body: ${rawText.slice(0, 200)})`);
            }
        } catch (err) {
            console.error('Error loading stats data:', err);
            const msg = typeof err === "string"     ? err
                      : err?.message                ? err.message
                      : err                         ? String(err)
                      : 'Failed to load stats — network error or CORS';
            setStatsError(msg);
        } finally {
            setStatsLoading(false);
        }
    };

    const loadConversionEvents = async () => {
        try {
            const response = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/event-conversations`);
            const list = response?.data?.event_conversations || [];
            const options = list.map((e) => ({ value: e._id, label: e.event_name, eventData: e }));
            setConversionEvents(options);
        } catch (err) {
            console.error('Error loading conversion events:', err);
        }
    };

    const [conversionEventSaving, setConversionEventSaving] = useState(false);

    const handleConversionEventChange = (option) => {
        setSelectedConversionEvent(option);
    };

    const saveConversionEvent = async () => {
        try {
            setConversionEventSaving(true);
            const response = await fetchWrapper.put(`${process.env.REACT_APP_API_URL}/campaigns/${id}`, {
                conversion_event: selectedConversionEvent ? selectedConversionEvent.value : null,
            });
            if (response?.success === false) {
                toast({ title: response.message, status: 'error', duration: 4000, isClosable: true });
            } else {
                toast({ title: 'Conversion event saved', status: 'success', duration: 2000 });
            }
        } catch (err) {
            toast({ title: err?.message || 'Failed to save', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setConversionEventSaving(false);
        }
    };

    const loadOverviewData = async () => {
        try {
            setOverviewLoading(true);
            setOverviewError(null);

            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}/analytics/overview`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setOverviewData(response.data);
            } else {
                setOverviewError('Failed to load campaign data');
            }
        } catch (err) {
            console.error('Error loading overview data:', err);
            setOverviewError('Failed to load campaign data');
        } finally {
            setOverviewLoading(false);
        }
    };

    const loadPerformanceData = async () => {
        try {
            setPerformanceLoading(true);
            setPerformanceError(null);

            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}/analytics/performance`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setPerformanceData(response.data);
            } else {
                setPerformanceError('Failed to load performance data');
            }
        } catch (err) {
            console.error('Error loading performance data:', err);
            setPerformanceError('Failed to load performance data');
        } finally {
            setPerformanceLoading(false);
        }
    };

    const loadChannelsData = async () => {
        try {
            setChannelsLoading(true);
            setChannelsError(null);

            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}/analytics/channels`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setChannelsData(response.data);
            } else {
                setChannelsError('Failed to load channels data');
            }
        } catch (err) {
            console.error('Error loading channels data:', err);
            setChannelsError('Failed to load channels data');
        } finally {
            setChannelsLoading(false);
        }
    };

    const loadMessageData = async (page = 1, limit = 50, status = '', channel = '', search = '') => {
        try {
            setMessageLoading(true);
            setMessageError(null);

            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(status && { status }),
                ...(channel && { channel }),
                ...(search && { search })
            });

            const url = `${process.env.REACT_APP_API_URL}/campaigns/${id}/analytics/messages?${params}`;
            const response = await fetchWrapper.get(url);

            if (response.success) {
                setMessageData(response.data);
            } else {
                setMessageError('Failed to load message data');
            }
        } catch (err) {
            console.error('Error loading message data:', err);
            setMessageError('Failed to load message data');
        } finally {
            setMessageLoading(false);
        }
    };

    const handleTabChange = (index) => {
        setActiveTab(index);

        // Load data based on active tab
        switch (index) {
            case 1: // Performance
                if (!performanceData) {
                    loadPerformanceData();
                }
                break;
            case 2: // Channels
                if (!channelsData) {
                    loadChannelsData();
                }
                break;
            case 3: // Message Details
                if (!messageData) {
                    loadMessageData();
                }
                break;
            default:
                break;
        }
    };

    const handleNavigateBack = () => {
        navigate("/marketing/campaign");
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (overviewLoading || statsLoading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Box p="50px" textAlign="center">
                        <Spinner size="xl" color="blue.500" />
                        <Text mt={4} color="gray.600">Loading campaign data...</Text>
                    </Box>
                </BoxWrapper>
            </>
        );
    }

    if (overviewError) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Box p="50px">
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            {overviewError}
                        </Alert>
                        <Button mt={4} onClick={() => { loadStatsData(); loadOverviewData(); }} colorScheme="blue">
                            Retry
                        </Button>
                    </Box>
                </BoxWrapper>
            </>
        );
    }

    const formatCompactPrice = (num, currency = '') => {
        const n = Number(num);
        if (isNaN(n)) return '—';
        return `${currency} ${n.toFixed(2)}`;
    };

    const campaign_info = statsData?.campaign_info;
    const campaignHeader = statsData?.header;
    const getStatusBadge = (status) => {
        // Defensive: status can be undefined on first render (header API
        // hasn't resolved yet) or when the row genuinely has no status.
        // Callsites use optional chaining but pass undefined straight in,
        // which would crash .toLowerCase() / .charAt(). (DA-BUG-0229.)
        const safe = (status == null ? "" : String(status)).trim();
        if (!safe) {
            return (
                <CustomBadge
                    px={2}
                    py={.8}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.700"
                    bg="gray.200"
                >
                    —
                </CustomBadge>
            );
        }
        const normalizedStatus = safe.toLowerCase();

        const statusConfig = {
            completed: { color: "green.800", bg: "#b9f8cf" },
            running: { color: "green.800", bg: "#b9f8cf" },
            scheduled: { color: "blue.800", bg: "blue.100" },
            draft: { color: "gray.700", bg: "gray.200" },
            failed: { color: "red.800", bg: "red.100" },
            paused: { color: "orange.800", bg: "orange.100" },
            cancelled: { color: "red.800", bg: "red.100" },
        };

        const { color, bg } = statusConfig[normalizedStatus] || {
            color: "gray.700",
            bg: "gray.200",
        };

        return (

            <CustomBadge
                px={2}
                py={.8}
                borderRadius="full"
                fontSize="sm"
                fontWeight="medium"
                color={color}
                bg={bg}

            >
                {safe.charAt(0).toUpperCase() + safe.slice(1)}
            </CustomBadge>

        );
    };

    return (
        <>
            <Header header={header} />

            <BoxWrapper>
                <Box >

                    <Button

                        color="black"

                        variant="outline"
                        size="sm"
                        borderRadius="md"
                        _hover={{ bg: "#0000001a !important", color: "black !important" }}
                        onClick={handleNavigateBack}
                        my={4}
                    >
                        <LuArrowLeft style={{ marginRight: ".8rem" }} /> Back To Campaigns
                    </Button>
                    {/* <Flex direction="column">
                        <Flex alignItems={"center"}>
                            <Text fontSize="2xl" fontWeight="semibold" me={4}>
                                {campaign_info?.name || 'Campaign Details'}
                            </Text>
                            <Flex alignItems={"center"} justifyContent={"center"} mt={2}>
                                {getStatusBadge(campaign_info?.status)}
                            </Flex>
                            <Flex alignItems={"center"} justifyContent={"center"} mt={2} ms={2}>
                                <CustomBadge bg="white" py={"1.2"} style={{ borderRadius: "15px", border: "1px solid #0000001a" }} fontSize='xs' color='black'>{campaign_info.campaign_type}</CustomBadge>
                            </Flex>
                        </Flex>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                            Campaign ID: {campaign_info?.id} •
                            Created {formatDate(campaign_info?.created_at)}

                        </Text>
                    </Flex> */}

                </Box>
                {statsError && (
                    <Alert status="warning" borderRadius="md" mb={3}>
                        <AlertIcon />
                        <Box flex="1">
                            <Text fontWeight="600" fontSize="sm">Couldn't load Campaign Stats</Text>
                            <Text fontSize="xs" color="gray.700" mt={1}>{statsError}</Text>
                        </Box>
                        <Button size="xs" colorScheme="yellow" onClick={loadStatsData}>Retry</Button>
                    </Alert>
                )}
                <MetricsCards metrics={statsData?.metrics} data={statsData} />
                {/* Campaign Information — full width */}
                <Box className="box-style-card" my={4}>
                    <HStack mb={4} spacing={2}>
                        <Icon as={FiFileText} color="var(--brand-primary)" boxSize={5} />
                        <Text fontSize="lg" fontWeight="semibold">Campaign Information</Text>
                    </HStack>

                        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                            {/* Campaign ID */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Campaign ID</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.campaign_code}
                                </Text>
                            </Box>

                            {/* Template Name */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Template Name</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.template_name}
                                </Text>
                            </Box>

                            {/* Channel */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Channel</Text>
                                {(() => {
                                    const CHANNEL_BG = {
                                        [CHANNEL_IDS.SMS]: 'var(--brand-tint)',
                                        [CHANNEL_IDS.WHATSAPP]: '#f0fdf4',
                                        [CHANNEL_IDS.EMAIL]: '#faf5ff',
                                        [CHANNEL_IDS.VOICE]: '#fff7ed',
                                        [CHANNEL_IDS.IN_APP]: 'var(--brand-50)',
                                        [CHANNEL_IDS.PUSH]: '#fdf2f8',
                                    };
                                    const CHANNEL_META = CHANNELS
                                        .map(c => ({ ...c, bg: CHANNEL_BG[c.id] }))
                                        .sort((a, b) => a.id - b.id);
                                    const selectedIds = campaign_info?.selected_channels ?? [];
                                    const channels = CHANNEL_META.filter(c => selectedIds.includes(String(c.id)));
                                    if (!channels.length) return <Text fontSize="sm" color="gray.400">—</Text>;
                                    return (
                                        <HStack spacing={2} flexWrap="wrap">
                                            {channels.map((ch) => (
                                                <HStack
                                                    key={ch.id}
                                                    spacing={2}
                                                    px={2} py={1}
                                                    borderRadius="full"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    display="inline-flex"
                                                    bg={ch.bg}
                                                >
                                                    <Icon as={ch.icon} boxSize={3.5} color={ch.color} />
                                                    <Text fontSize="xs" fontWeight="medium" color={ch.color}>{ch.label}</Text>
                                                </HStack>
                                            ))}
                                        </HStack>
                                    );
                                })()}
                            </Box>

                            {/* Timezone */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Timezone</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.timezone}
                                </Text>
                            </Box>

                            {/* Start Time */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Start Time</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.start_time || '—'}
                                </Text>
                            </Box>

                            {/* End Time */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>End Time</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.end_time || '—'}
                                </Text>
                            </Box>

                            {/* Status */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Status</Text>
                                {getStatusBadge(campaignHeader?.status)}
                            </Box>

                            {/* Created On */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Created On</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.created_on || '—'}
                                </Text>
                            </Box>

                            {/* Created By */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Created By</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.created_by || '—'}
                                </Text>
                            </Box>

                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>SMS Sender</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.sms_sender || '—'}
                                </Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Email Sender</Text>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                    {campaign_info?.email_sender || '—'}
                                </Text>
                            </Box>

                            {/* Target Audience — spans full row */}
                            <Box >
                                <Text fontSize="xs" color="gray.500" mb={2}>Target Audience</Text>
                                {campaign_info?.audience_type === 'all_contacts' ? (
                                    <Tag size="sm" color="#6e11b0" bg="#f3e8ff" borderRadius="full" fontWeight="medium">All Contacts</Tag>
                                ) : campaign_info?.tags?.length > 0 ? (
                                    <HStack spacing={2} flexWrap="wrap">
                                        {campaign_info.tags.map((tag, i) => (
                                            <Tag key={i} size="sm" color="#6e11b0" bg="#f3e8ff" borderRadius="full" fontWeight="medium">
                                                {tag}
                                            </Tag>
                                        ))}
                                    </HStack>
                                ) : (
                                    <Text fontSize="sm" color="gray.500">—</Text>
                                )}
                            </Box>
                        </Grid>
                    </Box>

                {/* Cost, Preview, Conversion — three equal columns */}
                <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
                    {/* Campaign Cost Statistics */}
                    <Box className="box-style-card">
                        <HStack mb={4} spacing={2}>
                            <Icon as={FiCheckCircle} color="var(--brand-primary)" boxSize={5} />
                            <Text fontSize="lg" fontWeight="semibold">Campaign Cost Statistics</Text>
                        </HStack>
                        <Flex direction="column" gap={5}>
                            <Box>
                                <Text fontSize="sm" color="gray.500" mb={1}>
                                    Planned Cost
                                </Text>

                                <Text fontSize="sm" fontWeight="400" color="var(--brand-primary)">
                                    {statsData?.costs?.planned_cost?.channel_breakup?.length ? (
                                        statsData.costs.planned_cost.channel_breakup.map((item) => {
                                            return (
                                                <Flex
                                                    key={item.channel_id}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    width="100%"
                                                >
                                                    <Flex alignItems="center" gap={2}>
                                                        <Box
                                                            width="10px"
                                                            height="10px"
                                                            borderRadius="full"
                                                            bg="blue.400"
                                                        />
                                                        <Text>{item?.channel}</Text>
                                                    </Flex>

                                                    <Text>
                                                        {formatCompactPrice(
                                                            item?.cost ?? 0,
                                                            statsData?.costs?.currency ?? "PKR "
                                                        )}
                                                    </Text>
                                                </Flex>
                                            );
                                        })
                                    ) : (
                                        "—"
                                    )}
                                </Text>
                            </Box>
                            <Box>
                                <Text fontSize="sm" color="gray.500" mb={1}>Actual Total Cost</Text>
                                <Text fontSize="3xl" fontWeight="400" color="green.500">
                                    {statsData?.costs?.actual_total_cost != null
                                        ? formatCompactPrice(statsData.costs.actual_total_cost, statsData.costs.currency || 'PKR ')
                                        : '—'}
                                </Text>
                            </Box>
                        </Flex>
                    </Box>

                    {/* Message Preview */}
                    <Box className="box-style-card">
                        <HStack mb={4} spacing={2}>
                            <Icon as={FiMessageSquare} color="var(--brand-primary)" boxSize={5} />
                            <Text fontSize="lg" fontWeight="semibold">Message Preview</Text>
                        </HStack>
                        <MessagePreview
                            template={statsData?.campaign_info?.combine_messsage}
                            selectedChannelIds={statsData?.campaign_info?.selected_channels}
                        />
                    </Box>

                    {/* Conversion Event */}
                    <Box className="box-style-card">
                        <HStack mb={4} spacing={2}>
                            <Icon as={FiTag} color="var(--brand-primary)" boxSize={5} />
                            <Text fontSize="lg" fontWeight="semibold">Conversion Event</Text>
                        </HStack>
                        <Flex direction="column" gap={4}>
                            <Box>
                                <Text fontSize="sm" color="gray.500" mb={2}>Event</Text>
                                <Select
                                    options={conversionEvents}
                                    value={selectedConversionEvent}
                                    onChange={handleConversionEventChange}
                                    placeholder="Select event..."
                                    isClearable
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            fontSize: '13px',
                                            borderColor: '#e2e8f0',
                                            minHeight: '36px',
                                        }),
                                        option: (base) => ({
                                            ...base,
                                            fontSize: '13px',
                                        }),
                                    }}
                                />
                            </Box>
                            {selectedConversionEvent?.eventData?.value_config && (
                                <Box bg="gray.50" borderRadius="md" p={3} border="1px solid" borderColor="gray.200">
                                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                        Value Config
                                    </Text>
                                    <Flex direction="column" gap={2}>
                                        {Object.entries(selectedConversionEvent.eventData.value_config).map(([key, val]) => (
                                            <Flex key={key} justify="space-between" align="center">
                                                <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                                                    {key.replace(/_/g, ' ')}
                                                </Text>
                                                <Text fontSize="xs" fontWeight="600" color="gray.800">
                                                    {val ?? '—'}
                                                </Text>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </Box>
                            )}
                            <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Conversion Value</Text>
                                <Text fontSize="2xl" fontWeight="500" color="green.500">
                                    {statsData?.conversion_event?.conversion_value != null
                                        ? statsData.conversion_event.conversion_value
                                        : '—'}
                                </Text>
                            </Box>
                            <Button
                                size="sm"
                                borderRadius="md"
                                bg="var(--brand-primary)"
                                color="white"
                                _hover={{ bg: 'var(--brand-primary-hover)' }}
                                _active={{ bg: 'var(--brand-primary-active)' }}
                                isLoading={conversionEventSaving}
                                onClick={saveConversionEvent}
                            >
                                Save
                            </Button>
                        </Flex>
                    </Box>
                </Grid>

                <Box className="box-style-card">
                    <Tabs variant="unstyled" index={activeTab} onChange={handleTabChange}>
                        <Flex justify="space-between" align="center" mb={4}>
                            <HStack spacing={2}>
                                <Icon as={FiMessageSquare} color="var(--brand-primary)" boxSize={4} />
                                <Text fontWeight="medium">Campaign Details</Text>
                            </HStack>
                            <TabList
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="full"
                                p={1}
                                display="flex"
                            >
                                {[
                                    "Overview",
                                    "Performance",
                                    "Channels",
                                    "Message Details",
                                    // Hidden on the public demo — see AI_INSIGHTS_ENABLED. The
                                    // matching TabPanel below is gated on the same flag so Chakra
                                    // keeps an equal number of Tabs and TabPanels.
                                    ...(AI_INSIGHTS_ENABLED ? ["✨ AI Insights"] : []),
                                    // FEAT-ATTRIB-003 A2. Hidden when the attribution service has
                                    // no base URL, rather than shown and failing: CRA inlines an
                                    // unset var as the literal "undefined", so the tab would 404
                                    // against a host that does not exist. Same both-sides gating
                                    // as AI Insights above.
                                    ...(ATTRIBUTION_ENABLED ? ["Impact"] : []),
                                ].map(
                                    (tab, index) => (
                                        <Tab
                                            key={tab}
                                            _selected={tab.includes("AI Insights")
                                                ? { bgGradient: "linear(to-r, purple.500, blue.500)", color: "white !important", fontWeight: "600" }
                                                : { bg: "var(--brand-primary)", color: "white !important", fontWeight: "600" }}
                                            borderRadius="full"
                                            fontSize="sm"
                                        >
                                            {tab}
                                        </Tab>
                                    )
                                )}
                            </TabList>
                        </Flex>

                        <TabPanels>
                            <TabPanel>
                                <CampaignOverview campaignData={overviewData} />
                            </TabPanel>
                            <TabPanel>
                                <PerformanceChart
                                    performanceData={performanceData}
                                    loading={performanceLoading}
                                    error={performanceError}
                                    onRetry={loadPerformanceData}
                                />
                            </TabPanel>
                            <TabPanel>
                                <ChannelsTable
                                    channelsData={channelsData}
                                    loading={channelsLoading}
                                    error={channelsError}
                                    onRetry={loadChannelsData}
                                />
                            </TabPanel>
                            <TabPanel>
                                <MessageDetailsTable
                                    messageData={messageData}
                                    loading={messageLoading}
                                    error={messageError}
                                    onRetry={loadMessageData}
                                    onPageChange={loadMessageData}
                                />
                            </TabPanel>
                            {/* FEAT-0012 — AI Insights tab (replaces modal). Not rendered at all
                                when the flag is off, so the analyst endpoint is never called and
                                the panel count still matches the tab count. */}
                            {AI_INSIGHTS_ENABLED && (
                                <TabPanel>
                                    <AiInsightsTab campaignId={id} />
                                </TabPanel>
                            )}
                            {/* FEAT-ATTRIB-003 A2 — observed, credited, residual and the
                                MRC methodology disclosure. The goal is the campaign's own
                                declared conversion event (tbl_event_conversations._id),
                                which is what the new Campaign Manager already stores in
                                tbl_campaigns.conversion_event. */}
                            {ATTRIBUTION_ENABLED && (
                                <TabPanel>
                                    <ImpactTab
                                        goalDefinitionId={
                                            /* The page's own resolution order: the operator's
                                               selection wins, falling back to the goal stored on
                                               the campaign (tbl_campaigns.conversion_event). */
                                            selectedConversionEvent?.value
                                            || statsData?.conversion_event?.id
                                        }
                                    />
                                </TabPanel>
                            )}
                        </TabPanels>
                    </Tabs>
                </Box>
            </BoxWrapper>
        </>
    );
}